<template>
  <div class="phone-input--wrap relative">
    <div
      class="phone-input"
      :class="{
        'has-error': error,
        'phone-input--v2': v2,
        'is-disabled': disabled,
      }"
    >
      <div
        class="phone-input--country cursor-pointer flex items-center"
        :class="
          v2
            ? 'h-6 self-center gap-1 border-e'
            : 'h-full pl-3 rtl:pl-0 rtl:pr-3 justify-center gap-1.5 hover:bg-neutral-100'
        "
        @click="toggleCountryDropdown"
      >
        <div
          class="flex flex-row items-center gap-1"
          :class="{ 'text-gray-700': !v2 }"
          :dir="v2 ? 'ltr' : null"
        >
          <div
            v-if="showFlagInSelection && activeCountry && activeCountry.emoji"
            class="mr-1 rtl:mr-0 rtl:ml-1"
          >
            {{ activeCountry.emoji }}
          </div>
          <div :class="{ 'font-semibold': !v2 }">{{ activeCountryCode }}</div>
          <div>({{ activeDialCode }})</div>
        </div>
        <span
          class="phone-input--chevron material-symbols-outlined"
          :class="
            v2
              ? 'text-[24px]'
              : 'text-[20px] mt-[3px] pr-1 border-r rtl:pr-0 rtl:pl-1 rtl:border-r-0 rtl:border-l border-[#D1D5DB]'
          "
        >
          keyboard_arrow_down
        </span>
      </div>
      <input
        :value="phoneNumber"
        type="tel"
        class="phone-input--field rtl:text-right rtl:pl-3 ltr:pr-3"
        :placeholder="placeholder"
        :readonly="readonly"
        :disabled="disabled"
        :style="styles"
        @input="onChange"
        @focus="$emit('focus')"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="showDropdown"
      ref="dropdown"
      class="country-dropdown"
      :class="
        dropdownDirection === 'top' ? 'dropdown--top' : 'dropdown--bottom'
      "
    >
      <div v-if="enableSearchCountry" class="dropdown-search--wrap">
        <input
          ref="searchbar"
          v-model="searchCountry"
          type="text"
          :placeholder="$t('COMPONENTS.PHONE_INPUT.SEARCH_PLACEHOLDER')"
          class="dropdown-search"
        />
      </div>
      <div
        v-for="(country, index) in filteredCountriesBySearch"
        ref="dropdownItem"
        :key="index"
        class="country-dropdown--item"
        :class="{
          active: country.id === activeCountryCode,
          focus: index === selectedIndex,
        }"
        @click="onSelectCountry(country)"
      >
        <span class="text-base mr-1">{{ country.emoji }}</span>

        <span
          class="max-w-[7.5rem] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ country.name }}
        </span>
        <span class="ml-1 text-slate-300 dark:text-slate-300 text-xs">{{
          country.dial_code
        }}</span>
      </div>
      <div v-if="filteredCountriesBySearch.length === 0">
        <span
          class="flex items-center justify-center text-sm text-slate-500 dark:text-slate-300 mt-4"
        >
          {{ $t('COMPONENTS.PHONE_INPUT.NO_RESULTS') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import {
  hasPressedArrowUpKey,
  hasPressedArrowDownKey,
  isEnter,
} from 'shared/helpers/KeyboardHelpers';

export default {
  mixins: [eventListenerMixins],
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
    showFlagInSelection: {
      type: Boolean,
      default: true,
    },
    dropdownDirection: {
      type: String,
      default: 'bottom',
      validator: value => ['top', 'bottom'].includes(value),
    },
    defaultCountry: {
      type: String,
      default: 'IL',
    },
    onlyCountries: {
      type: Array,
      default: () => ['IL', 'US', 'ES'],
    },
    enableSearchCountry: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'combine',
      validator: value => ['combine', 'separate'].includes(value),
    },
    disableCountrySelect: {
      type: Boolean,
      default: false,
    },
    v2: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countries: countries.filter(country =>
        this.onlyCountries.includes(country.id)
      ),
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: '',
      activeDialCode: '',
      phoneNumber: this.value,
    };
  },
  computed: {
    filteredCountriesBySearch() {
      return this.countries.filter(country => {
        const { name, dial_code, id } = country;
        const search = this.searchCountry.toLowerCase();
        return (
          name.toLowerCase().includes(search) ||
          dial_code.toLowerCase().includes(search) ||
          id.toLowerCase().includes(search)
        );
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => country.id === this.activeCountryCode
        );
      }
      return '';
    },
  },
  watch: {
    value() {
      const number = parsePhoneNumber(this.value);
      if (number) {
        if (number.country) this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
        this.phoneNumber = number.nationalNumber;
      }
    },
  },
  mounted() {
    window.addEventListener('mouseup', this.onOutsideClick);
    this.setActiveCountry();
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onOutsideClick);
  },
  methods: {
    emitValue(dialCode, phoneNumber) {
      if (this.mode === 'combine') {
        this.$emit('input', `${dialCode}${phoneNumber}`);
      } else {
        this.$emit('input', phoneNumber, dialCode);
      }
    },

    onChange(e) {
      this.phoneNumber = e.target.value;
      this.emitValue(this.activeDialCode, e.target.value);
    },
    onBlur(e) {
      this.$emit('blur', e.target.value);
    },

    setActiveCountry() {
      const number = this.value ? parsePhoneNumber(this.value) : null;
      if (number) {
        if (number.country) this.activeCountryCode = number.country;
        this.activeDialCode = `+${number.countryCallingCode}`;
        this.phoneNumber = number.nationalNumber;
        return;
      }
      const countryId =
        this.countries.find(c => this.value?.startsWith(c.dial_code))?.id ??
        this.defaultCountry;
      const country = this.countries.find(c => c.id === countryId);
      if (country) {
        this.activeCountryCode = country.id;
        this.activeDialCode = country.dial_code;
      }
    },
    onSelectCountry(country) {
      this.activeCountryCode = country.id;
      this.activeDialCode = country.dial_code;
      this.searchCountry = '';
      this.$emit('setCode', country.dial_code);
      if (this.mode === 'combine') {
        this.emitValue(country.dial_code, this.phoneNumber);
      }
      this.closeDropdown();
    },

    toggleCountryDropdown() {
      if (this.disableCountrySelect || this.disabled) return;
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown && this.enableSearchCountry) {
        this.$nextTick(() => {
          this.$refs.searchbar.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
    onOutsideClick(e) {
      if (
        this.showDropdown &&
        e.target !== this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },

    onKeyDownHandler(e) {
      if (!this.showDropdown) return;

      if (hasPressedArrowDownKey(e)) {
        e.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.filteredCountriesBySearch.length - 1
        );
        this.$refs.dropdown.scrollTop = this.selectedIndex * 28;
      } else if (hasPressedArrowUpKey(e)) {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.$refs.dropdown.scrollTop = this.selectedIndex * 28 - 56;
      } else if (isEnter(e)) {
        e.preventDefault();
        const country =
          this.filteredCountriesBySearch[Math.max(this.selectedIndex, 0)];
        if (country) this.onSelectCountry(country);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.phone-input--wrap {
  .phone-input {
    @apply h-[44px] flex items-center justify-start rounded-[8px] border border-solid border-slate-200 dark:border-slate-600 overflow-hidden;

    &.has-error {
      @apply border border-solid border-[#B91C1C] dark:border-[#B91C1C];
    }

    &.phone-input--v2 {
      @apply px-[11px] gap-2 bg-white border-gray-300;

      .phone-input--country {
        @apply text-gray-700 border-gray-300;
      }

      .phone-input--chevron {
        @apply text-gray-500;
      }

      .phone-input--field {
        @apply p-0 bg-transparent text-base text-gray-700;

        &::placeholder {
          @apply text-gray-400;
        }
      }

      &:hover,
      &:focus-within {
        @apply border-gray-400;
      }

      &.is-disabled {
        @apply bg-neutral-100 border-neutral-300;

        .phone-input--country {
          @apply text-neutral-500 border-neutral-300 cursor-not-allowed;
        }

        .phone-input--chevron {
          @apply text-neutral-400;
        }

        .phone-input--field {
          @apply text-neutral-500 cursor-not-allowed;

          &::placeholder {
            @apply text-neutral-300;
          }
        }
      }

      &.has-error {
        @apply border-[#B91C1C];

        .phone-input--country {
          @apply text-[#B91C1C] border-[#B91C1C];
        }
      }
    }
  }

  .phone-input--field {
    @apply mb-0 border-0 h-full;
  }

  .country-dropdown {
    @apply z-10 absolute max-h-60 w-[12.5rem] rounded-[8px] shadow-md overflow-y-auto px-0 pt-0 bg-white dark:bg-slate-900 border border-gray-300;
    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-[#c1c1c1] rounded-full;
    }

    &::-webkit-scrollbar-thumb:hover {
      @apply bg-gray-400;
    }

    &.dropdown--bottom {
      @apply top-[48px];
    }

    &.dropdown--top {
      @apply bottom-[48px];
    }

    .dropdown-search--wrap {
      @apply top-0 sticky bg-white dark:bg-slate-900 p-1;

      .dropdown-search {
        @apply h-8 mb-0 text-sm border border-solid border-slate-200 dark:border-slate-600;
      }
    }

    .country-dropdown--item {
      @apply flex items-center py-2 px-3 cursor-pointer gap-1 hover:bg-neutral-100;

      &.active {
        @apply bg-slate-50 dark:bg-slate-700;
      }

      &.focus {
        @apply bg-slate-25 dark:bg-slate-800;
      }
    }
  }
}
</style>
